<template>
  <div class="SideScroll relative">
    <template v-if="desktopArrows">
      <button
        v-show="canScrollLeft"
        class="arrowBtn arrowDesktop hidden desk:flex absolute left-0 z-10"
        :style="positionStyle(desktopArrowPosition ?? 'middle')"
        @mousedown.prevent="onPress(-1, desktopArrowStep)"
        @mouseup="stopScroll"
        @mouseleave="stopScroll"
      >
        <img
          src="/icons/chevron-left.svg"
          class="w-12 h-12"
          alt=""
        >
      </button>
      <button
        v-show="canScrollRight"
        class="arrowBtn arrowDesktop hidden desk:flex absolute right-0 z-10"
        :style="positionStyle(desktopArrowPosition ?? 'middle')"
        @mousedown.prevent="onPress(1, desktopArrowStep)"
        @mouseup="stopScroll"
        @mouseleave="stopScroll"
      >
        <img
          src="/icons/chevron-right.svg"
          class="w-12 h-12"
          alt=""
        >
      </button>
    </template>

    <template v-if="mobileArrows">
      <button
        v-show="canScrollLeft"
        class="arrowBtn arrowMobile flex desk:hidden absolute left-0 z-10"
        :style="positionStyle(mobileArrowPosition ?? 'middle')"
        @mousedown.prevent="onPress(-1, mobileArrowStep)"
        @mouseup="stopScroll"
        @touchstart.prevent="onPress(-1, mobileArrowStep)"
        @touchend="stopScroll"
      >
        <img
          src="/icons/chevron-left.svg"
          class="w-12 h-12"
          alt=""
        >
      </button>
      <button
        v-show="canScrollRight"
        class="arrowBtn arrowMobile flex desk:hidden absolute right-0 z-10"
        :style="positionStyle(mobileArrowPosition ?? 'middle')"
        @mousedown.prevent="onPress(1, mobileArrowStep)"
        @mouseup="stopScroll"
        @touchstart.prevent="onPress(1, mobileArrowStep)"
        @touchend="stopScroll"
      >
        <img
          src="/icons/chevron-right.svg"
          class="w-12 h-12"
          alt=""
        >
      </button>
    </template>

    <div
      ref="scrollEl"
      class="sideScrollWrapper"
      :style="maskStyle"
    >
      <slot />
    </div>

    <!-- Solid color fade overlays (used when fadeOutColor is set) -->
    <div
      v-if="fadeOutColor && canScrollLeft"
      class="absolute top-0 left-0 h-full w-48 pointer-events-none"
      :style="{ background: `linear-gradient(to right, ${fadeOutColor}, transparent)` }"
    />
    <div
      v-if="fadeOutColor && canScrollRight"
      class="absolute top-0 right-0 h-full w-48 pointer-events-none"
      :style="{ background: `linear-gradient(to left, ${fadeOutColor}, transparent)` }"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * A simple component to wrap side swiping using only overflow.
 * For image swiper, use SimpleSwiper.
 *
 * The content must expand horizontally via fixed width or white-space: nowrap.
 *
 * Optional arrow buttons:
 * - desktopArrows / mobileArrows: enable arrows per device type
 * - desktopArrowStep / mobileArrowStep: scroll amount per click ('100px', '25%').
 *   Omit to enable continuous scroll while button is held.
 * - desktopArrowPosition / mobileArrowPosition: vertical position of arrow buttons
 */
const props = defineProps<{
  desktopArrows?: boolean;
  desktopArrowStep?: string;
  desktopArrowPosition?: 'top' | 'middle' | 'bottom';
  mobileArrows?: boolean;
  mobileArrowStep?: string;
  mobileArrowPosition?: 'top' | 'middle' | 'bottom';
  fadeOut?: boolean;
  fadeOutColor?: string;
}>();

const scrollEl = useTemplateRef('scrollEl');
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const updateScrollState = () => {
  if (!scrollEl.value) { return; }
  const { scrollLeft, scrollWidth, clientWidth } = scrollEl.value;
  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft + clientWidth < scrollWidth - 1;
};

onMounted(() => {
  if (!scrollEl.value) { return; }
  scrollEl.value.addEventListener('scroll', updateScrollState, { passive: true });
  const ro = new ResizeObserver(updateScrollState);
  ro.observe(scrollEl.value);
  updateScrollState();
  onUnmounted(() => ro.disconnect());
});

const FADE_SIZE = '48px';
const maskStyle = computed(() => {
  if (!props.fadeOut) { return {}; }
  const parts: string[] = [];
  if (canScrollLeft.value) {
    parts.push('transparent 0', `black ${FADE_SIZE}`);
  } else {
    parts.push('black 0');
  }
  if (canScrollRight.value) {
    parts.push(`black calc(100% - ${FADE_SIZE})`, 'transparent 100%');
  } else {
    parts.push('black 100%');
  }
  const mask = `linear-gradient(to right, ${parts.join(', ')})`;
  return { WebkitMaskImage: mask, maskImage: mask };
});

const parseStep = (step: string, containerWidth: number): number => {
  const s = step.trim();
  if (s.endsWith('%')) { return (parseFloat(s) / 100) * containerWidth; }
  return parseFloat(s);
};

let scrollTimer: ReturnType<typeof setInterval> | null = null;

const stopScroll = () => {
  if (scrollTimer !== null) {
    clearInterval(scrollTimer);
    scrollTimer = null;
  }
};

const onPress = (direction: 1 | -1, step?: string) => {
  if (!scrollEl.value) { return; }
  stopScroll();
  if (step) {
    const amount = parseStep(step, scrollEl.value.clientWidth);
    scrollEl.value.scrollBy({ left: direction * amount, behavior: 'smooth' });
  } else {
    scrollTimer = setInterval(() => {
      scrollEl.value!.scrollLeft += direction * 8;
      updateScrollState();
    }, 16);
  }
};

const positionStyle = (position: 'top' | 'middle' | 'bottom'): Record<string, string> => {
  if (position === 'top') { return { top: '0' }; }
  if (position === 'bottom') { return { bottom: '0' }; }
  return { top: '50%', transform: 'translateY(-50%)' };
};
</script>

<style lang="postcss">
.sideScrollWrapper {
  width: 100%;
  overflow: hidden;
  overflow-x: auto;
  appearance: none;
  @media (hover: none) {
    scrollbar-width: none; /* firefox */
    &::-webkit-scrollbar {
      width: 0; /* Remove scrollbar space */
      height: 0;
    }
  }
}
.sideScrollRightPad {
  @apply w-12 shrink-0 desk:hidden;
  &:after {
    content: '\a0';
  }
}

/* Arrow buttons */
.arrowBtn {
  @apply w-32 h-32 rounded-full bg-bg-base shadow-md items-center justify-center;
  transition: opacity 0.2s ease;
}

/* Desktop arrows: fade in on parent hover */
@media (hover: hover) {
  .arrowDesktop {
    opacity: 0;
    pointer-events: none;
  }
  .SideScroll:hover .arrowDesktop {
    opacity: 1;
    pointer-events: auto;
  }
}

/* Mobile arrows: always visible */
.arrowMobile {
  opacity: 1;
}
</style>
